// An id the app gives one save, kept until the server answers that save:
// the server returns what it already saved for a repeat id instead of
// saving it twice (a part, a dose).
export function newClientRef() {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  return Array.from({ length: 4 }, () => Math.random().toString(36).slice(2, 10)).join('');
}

// A retry comes within minutes of the save it repeats, or once the phone
// finds signal again. An id kept for good answered tomorrow's identical
// dose with today's, so it was never logged (the review of nightly sweep 2,
// 2026-10-24): an id lasts two hours from its latest try, then a new one is
// made. A twin past that (counted twice, so the next dose comes out
// smaller) is the safer miss than a dose given and never recorded.
export const CLAIM_TTL_MS = 2 * 60 * 60 * 1000;

// The same request again (same key, inside the window) keeps its id;
// anything else gets a new one.
export function pendingRequest(pending, key, make = newClientRef, now = Date.now()) {
  return pending && pending.key === key && now - pending.at < CLAIM_TTL_MS
    ? { ...pending, at: now }
    : { key, ref: make(), at: now };
}

// Kept per scope (a tank's dose log, one parameter's calculator, one piece
// of equipment) for the visit, not in the sheet: a sheet closed and opened
// again after a lost reply made a twin (nightly sweep 2, 2026-10-24).
// Settled only by a saved answer: a dose seen in a list is not spent, since
// the keeper who saw "may already be saved" and taps Save again means the
// same dose. Keeping an id after a refusal is harmless: nothing was saved
// under it, so its retry is a first save.
const PENDING = new Map();
export function claimRequest(scope, key, make = newClientRef, now = Date.now()) {
  const p = pendingRequest(PENDING.get(scope), key, make, now);
  PENDING.set(scope, p);
  return p.ref;
}
export function settleRequest(scope) {
  PENDING.delete(scope);
}

// No answer, or a server error after it may have saved (a deploy cutting the
// reply, a later step failing): the keeper is told it may be saved and that
// tapping again saves it once, never a bare "HTTP 502".
export function unsureSaveWords(e) {
  const status = Number(e?.status);
  const unanswered =
    e?.network === true ||
    /^(failed to fetch|load failed|networkerror)/i.test(e?.message || '') ||
    (Number.isFinite(status) && status >= 500);
  return unanswered
    ? 'No answer from TankSensei, so it may already be saved. Tap again when you have signal: it is saved once.'
    : null;
}

// A note or log entry answered as the one already saved (duplicate) that
// already holds as many photos as this try sends: they went with the first
// try, so they are not sent twice.
export function alreadyHasPhotos(row, sending) {
  return row?.duplicate === true && Number(row.image_count) >= Math.max(1, Number(sending) || 0);
}
